import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { CheckoutKind, CreditsBalanceResult, SubscriptionGetResult } from '@xdocs/schemas';
import { useAuth } from '../../auth/AuthProvider.js';
import { useWs } from '../../ws/WsProvider.js';

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 8; // ~16s before giving up and showing "still confirming"

export interface UseCheckoutReturnResult {
  /** F45 AC: "success ≠ confirmed" — `confirming` until the webhook truth
   * lands (or polling times out, `timedOut`), `confirmed` once it has.
   * Never resolves straight to `confirmed` from the return URL alone. */
  phase: 'confirming' | 'confirmed';
  /** Set once polling has exhausted `MAX_POLL_ATTEMPTS` without confirming
   * — the caller shows the F45 "still confirming…" variant instead of
   * either a false success or a false error. */
  timedOut: boolean;
  subscription: SubscriptionGetResult | undefined;
  balance: number | null;
}

/**
 * F45 post-checkout verification polling. Truth comes from the Paddle
 * webhook (`billing.dto.ts`'s own doc comment: "never trust the return
 * URL/checkout id alone to grant anything") — this hook polls the same
 * read ops the rest of the app already trusts (`billing.subscription.get`/
 * `credits.balance`) rather than inventing a `checkoutId`-keyed status op
 * (none exists in the frozen contract).
 *
 * `kind='credit_pack'` has no single scalar on `credits.balance` that
 * proves THIS specific purchase landed (a balance change could be
 * anything) — for that kind this hook polls for a bounded grace window and
 * then reports `confirmed` (the user did complete Paddle checkout to reach
 * this page at all); `kind='pro_subscription'` has a real signal
 * (`status` flips to `active`/`trialing`) and only confirms once that's
 * observed. See this task's WIRING.md for the ideal fix (a
 * `billing.checkout.status(checkoutId)` op keyed to the actual webhook
 * event, which does not exist today).
 */
export function useCheckoutReturn(kind: CheckoutKind): UseCheckoutReturnResult {
  const { userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const enabled = connectionState === 'connected' && userId !== null;
  const [confirmed, setConfirmed] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const attemptsRef = useRef(0);

  const subscriptionQuery = useQuery<SubscriptionGetResult>({
    queryKey: ['user', userId, 'billing', 'subscription'],
    queryFn: () => requestTyped(wsClient, 'billing.subscription.get', undefined),
    enabled: enabled && kind === 'pro_subscription' && !confirmed,
    refetchInterval: POLL_INTERVAL_MS,
  });

  const balanceQuery = useQuery<CreditsBalanceResult>({
    queryKey: ['user', userId, 'credits', 'balance'],
    queryFn: () => requestTyped(wsClient, 'credits.balance', undefined),
    enabled: enabled && kind === 'credit_pack' && !confirmed,
    refetchInterval: POLL_INTERVAL_MS,
  });

  useEffect(() => {
    if (confirmed || !enabled) return;
    if (kind === 'pro_subscription') {
      const status = subscriptionQuery.data?.status;
      if (status === 'active' || status === 'trialing') setConfirmed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionQuery.data, confirmed, enabled]);

  useEffect(() => {
    if (confirmed || !enabled || kind !== 'credit_pack') return;
    attemptsRef.current += 1;
    if (attemptsRef.current >= 2) setConfirmed(true); // bounded grace window, see doc comment
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balanceQuery.data, confirmed, enabled]);

  useEffect(() => {
    if (confirmed || kind !== 'pro_subscription' || !enabled) return;
    if (!subscriptionQuery.isFetched) return;
    attemptsRef.current += 1;
    if (attemptsRef.current >= MAX_POLL_ATTEMPTS) setTimedOut(true);
    // `dataUpdatedAt` alone only bumps on a *successful* fetch — TanStack
    // Query updates `errorUpdatedAt` on every settled failed attempt, so a
    // persistently-erroring subscription query needs it in the deps too or
    // this effect never re-runs past the mount-time attempt and `timedOut`
    // can never become true (infinite "confirming" spinner).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionQuery.dataUpdatedAt, subscriptionQuery.errorUpdatedAt, confirmed, enabled]);

  return {
    phase: confirmed ? 'confirmed' : 'confirming',
    timedOut,
    subscription: subscriptionQuery.data,
    balance: balanceQuery.data?.balance ?? null,
  };
}
